import { FC } from 'react'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/legacy/image'
import Button from '@components/ui/IndigoButton'
import cartHandler from '@components/services/cart'
import { useUI } from '@components/ui/context'
import axios from '@services/axiosInterceptorInstance'

import {
  AppsFlyerKeys,
  AppsFlyerParams,
  EmptyGuid,
  FacebookEvents,
  Messages,
  NEXT_CREATE_WISHLIST,
} from '@components/utils/constants'
import { round } from '@framework/utils/lodash'
import { groupBy } from '@framework/utils/lodash'
import SwiperCore, { Pagination } from 'swiper'
import {
  ADDED_TO_WISH,
  GENERAL_ADD_TO_BAG,
  INSUFFICIENT_STOCK,
  PROD_ADDED_SUCCESSFULLY,
  SEARCH_ITEM_CLICKED_POSITION,
} from '@components/utils/textVariables'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import {
  BTN_NOTIFY_ME,
  BTN_PRE_ORDER,
  GENERAL_ADD_TO_BASKET,
  IMG_PLACEHOLDER,
  QUICK_VIEW,
} from '@components/utils/textVariables'
import {
  priceFormat,
  getMarginFn,
  matchStrings,
} from '@framework/utils/parse-util'
import {
  getBrowserIdForFBConversion,
  getCommonAppsFlyerValue,
  getCurrentPage,
  getDateOfBirthHash,
  getGenderHash,
  getIpAddressForFBConversion,
  getCategories,
  getColor,
  handleBrokenImageError,
  isCartAssociated,
  openLoginView,
  openNewCartPage,
  removePrecedingSlash,
  removeSucceedingSlug,
  validateAddToCart,
  getColorAndSize,
} from '@framework/utils/app-util'
import { IExtraProps } from '@components/common/Layout/Layout'
import { useRouter } from 'next/router'
import useSection from 'hooks/useSection'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'
import { MO_ENGAGE_EVENT } from 'hooks/moengage_event.modal'
import { facebookEventHandler } from 'hooks/facebook-conversion'
import { getSHA256Hash } from '@framework/utils/cipher'
import { IProduct } from '@framework/api/operations/helpers/get-all-products.model'
import { useGoogleAnalyticsEvent } from 'hooks/useGAEvent'
import { generateUri } from '@commerce/utils/uri-util'

SwiperCore.use([Pagination])
declare const window: any
declare const AF: any

interface Props {
  product: IProduct
  collectionName: any
  position: any
  collectionId: any
  routerFilters?: any
  currentPageNumber?: string | number
  _plpmsg1Height?: any
  _plpmsg3Height?: any
  setQuickViewProductSlug: (slug: string | null) => void
  isFromSearch?: boolean
  showSizePopup?: any
}

const NewProductCard: FC<Props & IExtraProps> = ({
  product,
  collectionName,
  position,
  collectionId,
  routerFilters = null,
  deviceInfo,
  _plpmsg1Height,
  _plpmsg3Height,
  setQuickViewProductSlug,
  isFromSearch = false,
  showSizePopup,
}) => {
  const [currentProductData, setCurrentProductData] = useState({
    image: product?.image,
    link: product?.slug,
  })
  const trackMoEngageEvent = useMoEngageEvent()
  const { handleSection } = useSection()
  const {
    openCart,
    basketId,
    user,
    cartItems,
    addToWishlist,
    setCartItems,
    openNotifyUser,
    setNotifyProduct,
    setAlert,
    wishListItems,
    setSidebarView,
  } = useUI()
  const isOnlyMobile = deviceInfo?.isOnlyMobile
  const { isMobile, isIPadorTablet } = deviceInfo
  const { asPath } = useRouter()
  const router = useRouter()
  const trackGoogleAnalyticsEvent = useGoogleAnalyticsEvent()
  const isChannelStore = router?.query?.channelStore ? true : false

  const insertToLocalWishlist = () => {
    addToWishlist(product)
    setAlert({ type: 'success', msg: ADDED_TO_WISH })
    // openWishlist()
  }
  const colorName = product?.attributes?.filter(
    (x: any) => x?.key == 'global.colour'
  )
  let currentPage = getCurrentPage()
  const onViewApiKey = (quickViewProductSlug: string) => {
    setQuickViewProductSlug(quickViewProductSlug)
    let deviceCheck = ''
    if (isMobile || isIPadorTablet) {
      deviceCheck = 'Mobile'
    } else {
      deviceCheck = 'Desktop'
    }

    recordGA4Event(window, GA_EVENT.QUICK_VIEW_CLICK, {
      ecommerce: {
        device: deviceCheck,
        items: {
          product_name: product?.name,
          position: position,
          product_price: product?.price?.raw?.withTax,
          color: product?.attributes?.find(
            (x: any) => x?.key === 'product.colours'
          )?.value,
          category: product?.classification?.mainCategoryName,
          current_page: currentPage,
          header: collectionName,
        },
      },
    })
    recordGA4Event(window, GA_EVENT.POPUP_VIEW, {
      product_name: product?.name,
      category: product?.classification?.mainCategoryName,
      page: window?.location?.href,
      position: position,
      color: product?.attributes?.find((x: any) => x?.key === 'product.colours')
        ?.value,
      price: product?.price?.raw?.withTax,
      current_page: currentPage,
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.QUICK_VIEW,
      {
        URL: window?.location?.href,
        Product_name: product?.name,
        Product_category: product?.classification?.mainCategoryName,
        Product_color: product?.variantGroupCode,
        Price: product?.price?.raw?.withTax,
      },
      window
    )
  }

  const isInWishList = wishListItems
    ?.map((wi: any) => wi?.recordId)
    ?.includes(product?.recordId)

  const handleWishList = async () => {
    const accessToken = localStorage?.getItem('user')
    if (accessToken) {
      const createWishlist = async () => {
        try {
          const res = await axios.post(NEXT_CREATE_WISHLIST, {
            id: user?.userId,
            productId: product?.recordId,
            flag: true,
          })
          if (res?.data) {
            insertToLocalWishlist()
          }
        } catch (error) {
          console.error(':: error ', error)
        }
      }
      createWishlist()
    } else {
      openLoginView(asPath)
      setSidebarView('LOGIN_VIEW')
      // insertToLocalWishlist()
    }

    let productAvailability = 'Yes'
    if (product?.currentStock > 0) {
      productAvailability = 'Yes'
    } else {
      productAvailability = 'No'
    }

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.ADD_TO_WISHLIST,
      {
        URL: window?.location?.href,
        Product_name: product?.name,
        Product_category: product?.classification?.mainCategoryName,
        Product_category_2: product?.classification?.category,
        Product_color: product?.variantGroupCode,
        Price: product?.price?.raw?.withTax,
      },
      window
    )
    recordGA4Event(window, GA_EVENT.ADD_TO_WISHLIST, {
      ecommerce: {
        items: [
          {
            item_name: product?.name,
            item_brand: product?.brand,
            item_category: product?.classification?.mainCategoryName,
            item_category2: product?.classification?.category,
            item_variant: product?.attributes?.find(
              (x: any) => x?.key === 'product.colours'
            )?.value,
            quantity: 1,
            item_id: product?.sku,
            price: product?.price?.raw?.withTax,
            item_var_id: product?.stockCode,
          },
        ],
        header: collectionName,
        current_page: currentPage,
        availability: productAvailability,
      },
    })

    const mobileNumber = user?.mobile || user?.telephone
    let facebookeventData: any = {
      data: [
        {
          event_name: FacebookEvents.ADD_TO_WISHLIST,
          event_time: Math.floor(new Date().getTime() / 1000),
          action_source: 'website',
          user_data: {
            em: [user?.email ? await getSHA256Hash(user?.email) : ''],
            ph: mobileNumber ? await getSHA256Hash(mobileNumber) : null,
            fn: user?.firstName ? await getSHA256Hash(user?.firstName) : null,
            ln: user?.lastName ? await getSHA256Hash(user?.lastName) : null,
            client_user_agent: window.navigator.userAgent,
            fbp: getBrowserIdForFBConversion(),
            client_ip_address: (await getIpAddressForFBConversion()) || null,
            external_id: mobileNumber
              ? await getSHA256Hash(mobileNumber)
              : null,
            db: await getDateOfBirthHash(user),
            ge: await getGenderHash(user?.gender),
            ct: null,
            zp: null,
            country: await getSHA256Hash('in'),
          },
          custom_data: {
            currency: 'INR',
            content_name: getCurrentPage(),
            content_ids: [product?.stockCode],
            content_type: 'product',
            contents: [
              {
                id: product?.recordId,
                quantity: 1,
                item_price: product?.price?.raw?.withTax,
                delivery_category: 'home_delivery',
              },
            ],
          },
        },
      ],
    }

    facebookEventHandler(facebookeventData)

    if (typeof AF !== 'undefined') {
      const commonEventValue = getCommonAppsFlyerValue()
      AF('pba', 'event', {
        eventType: 'EVENT',
        eventName: AppsFlyerKeys.AddToWishlist,
        eventRevenueCurrency: 'INR',
        eventValue: {
          [AppsFlyerParams.AFEventParam3]: currentPage,
          [AppsFlyerParams.AFEventParamContent]: product?.name,
          [AppsFlyerParams.AFEventParamContentId]: product?.stockCode,
          [AppsFlyerParams.AFEventParamPrice]: product?.price?.raw?.withTax + '',
          [AppsFlyerParams.AFEventParam5]:
            product?.classification?.mainCategoryName || '',
          [AppsFlyerParams.AFEventParam6]:
            product?.classification?.category || '',
          ...commonEventValue,
        },
      })
    }
  }

  useEffect(() => {
    setCurrentProductData((prevState): any => {
      if (prevState.link !== product?.slug) {
        return { ...prevState, image: product?.image, link: product?.slug }
      } else return { ...prevState }
    })
  }, [product?.slug])

  const secondImage = product?.secondImage

  const handleHover = (type: string) => {
    if (type === 'enter' && secondImage)
      setCurrentProductData({ ...currentProductData, image: secondImage })
    if (type === 'leave' && secondImage)
      setCurrentProductData({ ...currentProductData, image: product?.image })
  }

  const handleNotification = () => {
    openNotifyUser(product?.recordId)
    setNotifyProduct(product)
  }

  const getlineItemSizeWithoutSlug = (product: any) => {
    if (isChannelStore && product?.size !== '' && product?.size !== undefined) {
      return product?.size
    } else if (isChannelStore && product?.customAttributes) {
      return product?.customAttributes
        ?.filter((vp: any) => vp?.key === 'clothing.size')?.[0]
        ?.fieldText?.toUpperCase()
        ?.replaceAll('-', '')
        ?.replaceAll('SIZE', '')
    } else {
      return product?.variantProducts
        ?.filter((vp: any) => vp?.stockCode === product?.stockCode)?.[0]
        ?.variantAttributes?.filter(
          (va: any) => va?.fieldCode === 'clothing.size'
        )?.[0]
        ?.fieldValue?.toUpperCase()
        ?.replaceAll('-', '')
        ?.replaceAll('SIZE', '')
    }
  }

  const addToCartGAEvent = (productData: any) => {
    trackGoogleAnalyticsEvent(
      GA_EVENT.ADD_TO_CART,
      {
        ecommerce: {
          items: [
            {
              item_name: productData?.name,
              item_brand: productData?.brand,
              item_category2: getCategories(productData).category2,
              item_category: getCategories(productData).category,
              item_variant: getColorAndSize(productData),
              color: getColor(productData),
              quantity: productData?.qty,
              item_id: productData?.sku,
              item_size: getlineItemSizeWithoutSlug(productData),
              price: productData?.price?.raw?.withTax,
              item_var_id: productData?.stockCode,
              item_list_name: getCategories(productData).category,
              index: 1,
            },
          ],
          cart_quantity: 1,
          total_value: productData?.price?.raw?.withTax,
          current_page: getCurrentPage(),
          section_title: 'Product Detail',
          loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
        },
      },
      window
    )
  }

  const buttonTitle = () => {
    let buttonConfig: any = {
      title: GENERAL_ADD_TO_BASKET,
      validateAction: async () => {
        const isValid = validateAddToCart(product?.recordId, cartItems)
        if (!isValid) {
          setAlert({
            type: 'error',
            msg: Messages.Errors['CART_ITEM_QTY_LIMIT_EXCEEDED'],
          })
        }
        return isValid
      },
      action: async () => {
        const item = await cartHandler().addToCart(
          {
            basketId,
            productId: product?.recordId,
            qty: 1,
            // manualUnitPrice: product?.price?.raw?.withTax,
            stockCode: product?.stockCode,
            userId: user?.userId,
            isAssociated: isCartAssociated(cartItems), //user?.isAssociated,
          },
          'ADD',
          { product },
          (item: any) => {
            // setCartItems(item)
            // openCart()
          }
        )
        // setCartItems(item)
        if (
          item?.message &&
          !matchStrings(item?.message, PROD_ADDED_SUCCESSFULLY, true)
        ) {
          setAlert({ type: 'error', msg: INSUFFICIENT_STOCK })
        } else if (item?.id === EmptyGuid) {
          setAlert({ type: 'error', msg: INSUFFICIENT_STOCK })
        } else {
          // setAlert({ type: 'success', msg: PROD_ADDED })
          addToCartGAEvent(product)
          setCartItems(item)
          //           openCart();
          openNewCartPage(openCart, 'item_added')
        }
      },
      shortMessage: '',
    }
    if (!product?.currentStock && !product?.preOrder?.isEnabled) {
      buttonConfig.title = BTN_NOTIFY_ME
      buttonConfig.isNotifyMeEnabled = true
      buttonConfig.action = async () => handleNotification()
      buttonConfig.buttonType = 'button'
    } else if (!product?.currentStock && product?.preOrder?.isEnabled) {
      buttonConfig.title = BTN_PRE_ORDER
      buttonConfig.isPreOrderEnabled = true
      buttonConfig.buttonType = 'button'
      // buttonConfig.shortMessage = product?.preOrder?.shortMessage
    }
    return buttonConfig
  }

  const setPositionValueForSearch = () => {
    if (currentPage == 'Search') {
      localStorage.setItem(SEARCH_ITEM_CLICKED_POSITION, position)
    }
  }

  function viewProductDetail(product: any) {
    let color = ''
    if (product?.variantGroupCode) {
      color = product?.variantGroupCode?.split('-')?.[1]
    }
    recordGA4Event(window, GA_EVENT.VIEW_PROD_DETAILS, {
      category_selected: product?.name,
      header: collectionName,
      current_page: currentPage,
    })

    recordGA4Event(window, GA_EVENT.SELECT_ITEM, {
      ecommerce: {
        items: [
          {
            item_id: product?.sku,
            item_name: product?.name,
            price: product?.price?.raw?.withTax,
            item_brand: product?.brand,
            item_category: product?.classification?.mainCategoryName,
            item_category2: product?.classification?.category,
            item_variant: product?.attributes?.find(
              (x: any) => x?.key === 'product.colours'
            )?.value,
            item_list_name: product?.classification?.category,
            item_list_id: collectionId,
            index: position,
          },
        ],
        color,
        position: position,
        item_var_id: product?.stockCode,
        current_page: currentPage,
        section_title: collectionName,
      },
    })
    setPositionValueForSearch()
    handleSection('PLP')
  }

  const buttonConfig = buttonTitle()
  const saving = product?.price?.maxPrice - product?.price?.minPrice
  const discount = round((saving / product?.price?.maxPrice) * 100, 0)
  const labelGroup = groupBy(product?.attributes, 'key')
  const productColor = product?.attributes?.filter(
    (x: any) => x?.key === 'product.colours'
  )
  let productName = product?.name
  if (productColor?.[0]?.value != undefined) {
    productName = product?.name + ' ' + productColor?.[0]?.value
  }

  const getMargin = getMarginFn(isOnlyMobile, position)

  const css = { maxWidth: '100%', height: 'auto' }
  // Return early if `product` is not defined
  if (!product) return <></>;
  return (
    <>
      <div className="relative mb-1 transition duration-300 origin-center group hover:bg-white main-card-hover">
        <div
          key={`${product?.recordId}-search-product`}
          className={`px-0 pt-0 mt-3 sm:group-hover:card-hover-shadow group-hover:pb-0 sm:px-0 ${getMargin}`}
        >
          <Link
            legacyBehavior
            passHref
            href={{
              pathname: `/${removeSucceedingSlug(currentProductData?.link)}/[slug]`,
              query: {
                state: JSON.stringify(routerFilters || ''),
              },
            }}
            as={{
              pathname: `/${removePrecedingSlash(currentProductData?.link)}`,
            }}
          >
            <a
              href={`/${removePrecedingSlash(currentProductData?.link)}`}
              onClick={(ev: any) => { 
                ev.preventDefault(); 
                viewProductDetail(product); 
                router.push(`/${removePrecedingSlash(currentProductData?.link)}`) 
              }}
            >
              <div className="relative z-0 overflow-hidden bg-gray-200 radius-xs aspect-w-1 aspect-h-1">
                <div className="imae-container">
                  <img
                    src={
                      generateUri(product?.image, 'h=500&fm=webp') ||
                      IMG_PLACEHOLDER
                    }
                    alt={product?.name}
                    onMouseEnter={() => handleHover('enter')}
                    onMouseLeave={() => handleHover('leave')}
                    style={css}
                    width={284}
                    height={505}
                    className="object-cover object-center w-full h-full radius-xs sm:h-full"
                    onError={(e: any) => handleBrokenImageError(e)}
                  />
                  {labelGroup && (
                    <span className="absolute left-0 z-10 inline-block uppercase badge-top-span top-2 sm:top-2 sm:group-hover:top-2">
                      {labelGroup['product.merchandisingconfig']
                        ?.slice(0, 1)
                        ?.map((attr: any, badgeId: number) => (
                          <span
                            className="inline-block px-4 py-1 text-xs text-white uppercase bg-orange badge-span-info"
                            key={`product-${badgeId}-new-badge`}
                          >
                            {attr?.value}
                          </span>
                        ))}
                    </span>
                  )}
                  {product?.rating > 0 && (
                    <div className="absolute left-0 z-10 inline-block uppercase bg-white bottom-2 sm:bottom-2 sm:group-hover:bottom-2">
                      <div
                        className="flex items-center justify-center"
                        style={{ padding: '2px 4px 2px 8px' }}
                      >
                        <span
                          className="mr-1 font-semibold text-12 text-primary dark:text-primary"
                          style={{ lineHeight: 'normal' }}
                        >
                          {product?.rating}
                        </span>
                        {/* <i className="sprite-icon sprite-star" /> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          fill="green"
                          className="bi bi-star-fill"
                          viewBox="0 0 16 16"
                        >
                          <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                {isFromSearch ? (
                  ' '
                ) : (
                  <>
                    {buttonConfig?.isPreOrderEnabled && (
                      <div className="absolute px-1 py-1 bg-yellow-400 rounded-sm top-2">
                        {BTN_PRE_ORDER}
                      </div>
                    )}
                    {buttonConfig?.isNotifyMeEnabled && (
                      <div className="absolute px-1 py-1 text-white bg-red-400 rounded-sm top-2">
                        {BTN_NOTIFY_ME}
                      </div>
                    )}
                  </>
                )}

                <span className="sr-only">{product?.name}</span>
              </div>
            </a>
          </Link>
          <div className={`w-full ${isOnlyMobile ? 'min-h-full' : 'hf-138'}`}>
            <div className="hidden p-2 sm:group-hover:block group-hover:pb-0 ipd-hidden">
              <h3
                className="font-medium text-12 text-[#2510008f] capitalize overflow-hidden whitespace-nowrap text-ellipsis"
                title={product?.name}
              >
                {product?.name}
              </h3>
            </div>
            <div className="pt-0">
              <h3
                className="px-2 pt-2 font-medium text-12 text-[#2510008f] sm:text-12 sm:group-hover:hidden ipd-visible"
                title={product?.name}
              >
                <Link
                  legacyBehavior
                  passHref
                  href={{
                    pathname: `/${removeSucceedingSlug(currentProductData?.link)}/[slug]`,
                    query: {
                      state: JSON.stringify(routerFilters),
                    },
                  }}
                  as={{
                    pathname: `/${removePrecedingSlash(currentProductData?.link)}`,
                  }}
                >
                  <a
                    href={`/${removePrecedingSlash(currentProductData?.link)}`}
                    className="capitalize text-truncate-two-lines"
                    onClick={(ev: any) => { 
                      ev.preventDefault()
                      router.push(`/${removePrecedingSlash(currentProductData?.link)}`)
                    }}
                  >
                    <p className={'mb-1 overflow-hidden whitespace-nowrap text-ellipsis'}>
                    {product?.name}
                    </p>
                  </a>
                </Link>
              </h3>

              <p className="min-h-full px-2 font-medium text-primary text-12 sm:mt-1 sm:group-hover:hidden mob-none-min-height sm:mb-0 ipd-visible">
                <Link
                  legacyBehavior
                  passHref
                  href={{
                    pathname: `/${removePrecedingSlash(
                      currentProductData?.link
                    )}`,
                    query: {
                      state: JSON.stringify(routerFilters),
                    },
                  }}
                >
                  <a
                    href={`/${removePrecedingSlash(currentProductData?.link)}`}
                  >
                    {priceFormat(product?.price?.minPrice)}
                    {product?.price?.maxPrice > 0 &&
                      product?.price?.maxPrice != product?.price?.minPrice && (
                        <>
                          <span
                            className="px-1 font-normal text-[#2510008f] line-through text-12"
                            style={{
                              textDecorationColor: '#2510008f',
                            }}
                          >
                            {priceFormat(product?.price?.maxPrice)}
                          </span>
                          <span className="inline-block text-12 text-[#00890E]">
                            {discount}% off
                          </span>
                        </>
                      )}
                  </a>
                </Link>
              </p>
              {labelGroup && <>
                <p
                  className="px-2 mt-1 mb-1 font-medium text-primary text-12 sm:mt-1 sm:group-hover:hidden sm:mb-0 ipd-visible"
                  style={{
                    minHeight: _plpmsg1Height,
                  }}
                >
                  {labelGroup['product.plpmsg1']
                    ?.slice(0, 1)
                    ?.map((attr: any, badgeId: number) => (
                      <p
                        key={`product-${badgeId}-new-badge`}
                        style={{
                          maxWidth: '100%',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        {attr?.value?.includes('@') ? (
                          <>
                            <span
                              className="text-xs font-bold text-orange"
                              style={{ fontSize: '0.625rem' }}
                            >
                              {attr?.value}
                            </span>
                            <span
                              className="text-[#2510008f]"
                              style={{ fontSize: '0.625rem' }}
                            >
                              {' '}
                              with coupon
                            </span>
                          </>
                        ) : (
                          <span
                            className="text-xs font-bold text-dark-red"
                            style={{ fontSize: '0.625rem' }}
                          >
                            {attr?.value}
                          </span>
                        )}
                      </p>
                    ))}
                </p>
                <p className="px-2 mt-1 mb-1 text-12 sm:mt-1 sm:group-hover:hidden sm:mb-0 ipd-visible" style={{ minHeight: _plpmsg3Height }} >
                  {labelGroup['product.plpmsg3']
                    ?.slice(0, 1)
                    ?.map((attr: any, badgeId: number) => (
                      <p
                        key={`product-${badgeId}-new-badge`}
                        style={{
                          maxWidth: '100%',
                        }}
                      >
                        <span className="font-semibold text-pretty plpMsg3" style={{ fontSize: '0.675rem' }}>{attr?.value}</span>
                      </p>
                    ))}
                </p>
              </>}
              <div className="flex flex-col">
                {/*Add to cart is hidden*/}
                <Button
                  className="hidden mt-2"
                  title={buttonConfig.title}
                  validateAction={buttonConfig.validateAction}
                  action={buttonConfig.action}
                  type="button"
                  buttonType={buttonConfig.buttonType || 'cart'}
                />
              </div>
              {isFromSearch ? (
                <div className="block sm:block lg:absolute inset-x-0 bottom-0 mx-1">
                  <button
                    onClick={() => {
                      showSizePopup(product?.slug, 'Upgrade')
                      setPositionValueForSearch()
                    }}
                    type="button"
                    className={
                      'flex items-center text-xs gap-2 justify-center w-full py-2 text-[#FFFFFF] bg-black'
                    }
                  >
                    <span>{GENERAL_ADD_TO_BAG}</span>
                  </button>
                </div>
              ) : (
                <div className="block sm:block lg:absolute inset-x-0 bottom-0 mx-2 overflow-hidden bg-white border sm:mx-0 sm:border-b-0 sm:border-l-0 sm:border-r-0 sm:border-t md:border-t lg:border-t sm:hidden sm:group-hover:block border-brown-light ipd-visible">
                  <div className="float-left w-4/12">
                    {isInWishList ? (
                      <div className="w-full px-2 pt-2 text-center bg-white sm:pt-3 add-wishlist">
                        <i className="sprite-icon sprite-o-whislist"></i>
                      </div>
                    ) : (
                      <button
                        className="w-full px-2 pt-2 text-center bg-white sm:pt-3 add-wishlist"
                        onClick={handleWishList}
                      >
                        <i className="sprite-icon sprite-product-whislist" />
                        <span className="text-sm font-medium text-gray-700 hover:text-red-800"></span>
                        <span className="sr-only">f</span>
                      </button>
                    )}
                  </div>
                  <div
                    className="float-left w-8/12 text-center sm:col-span-8"
                    id={`${product?.recordId}-plp-product`}
                  >
                    <button
                      type="button"
                      onClick={() => onViewApiKey(product?.slug)}
                      className="justify-center block w-full px-0 py-2 font-semibold text-center border-l text-14 text-primary dark:text-primary sm:px-3 sm:py-3 border-gray-light"
                    >
                      {QUICK_VIEW}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default NewProductCard
